import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "antd";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useChatbotStore } from "@/core/stores/chatbot.store";
import MessageList from "./components/MessageList";
import ChatInput, { type ChatInputHandle } from "./components/ChatInput";
import ChatPromptChips from "./components/ChatPromptChips";
import SuggestionCards from "./components/SuggestionCards";
import { useChatStream } from "./hooks/useChatStream";
import { deepseekClient } from "@/core/services/deepseek/DeepSeekClient";
import container from "@/lib/di/container";
import AuthRepo from "@/core/repos/auth.repo";

const authRepo = container.get(AuthRepo);

export default function ChatbotPage() {
  const navigate = useNavigate();
  const store = useChatbotStore();
  const authState = authRepo.useAuth();
  const [inputDraft, setInputDraft] = useState("");
  const chatInputRef = useRef<ChatInputHandle>(null);

  const activeConv = useMemo(
    () => store.conversations.find((c) => c.id === store.activeId) ?? null,
    [store.conversations, store.activeId]
  );

  useEffect(() => {
    document.title = "Trợ lý DVN";
  }, []);

  useEffect(() => {
    setInputDraft("");
  }, [store.activeId]);

  useEffect(() => {
    if (!store.activeId || !activeConv) {
      store.createConversation();
    }
  }, [store.activeId, activeConv, store]);

  const { sendMessage, stop, loading } = useChatStream(activeConv?.id ?? null);

  const isConfigured = deepseekClient.isChatConfigured();
  const hasMessages = (activeConv?.messages.length ?? 0) > 0;

  if (!authState) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white">
        <p className="text-sm text-slate-500">Đang kiểm tra phiên đăng nhập...</p>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-white">
      <header className="h-14 border-b border-slate-200 flex items-center px-4 md:px-6 gap-4 flex-shrink-0">
        <Button
          type="text"
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/")}
          className="!text-slate-600 hover:!text-slate-900"
        >
          <span className="hidden md:inline">Trang chủ</span>
        </Button>

        <div className="flex-1 flex items-center justify-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: "var(--primary-color)" }}
          />
          <span className="text-sm font-medium text-slate-900 tracking-tight">
            Trợ lý DVN
          </span>
        </div>

        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => store.createConversation()}
          className="!border-slate-200 !text-slate-700 hover:!border-slate-300 hover:!text-slate-900"
        >
          <span className="hidden md:inline">Cuộc trò chuyện mới</span>
        </Button>
      </header>

      {!isConfigured && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 md:px-6 py-2 text-xs text-amber-900 text-center">
          Chưa cấu hình API trợ lý — thêm{" "}
          <code className="font-mono">REACT_APP_CEREBRAS_API_KEY</code> (ưu tiên
          Cerebras + Qwen) và/hoặc{" "}
          <code className="font-mono">REACT_APP_DEEPSEEK_API_KEY</code> (dự phòng)
          vào <code className="font-mono">.env</code>, rồi khởi động lại.
        </div>
      )}

      {hasMessages ? (
        <MessageList messages={activeConv!.messages} />
      ) : (
        <SuggestionCards />
      )}

      <ChatPromptChips
        disabled={!isConfigured || loading}
        onPick={(prompt) => {
          setInputDraft(prompt);
          queueMicrotask(() => chatInputRef.current?.focus());
        }}
      />

      <ChatInput
        ref={chatInputRef}
        value={inputDraft}
        onChange={setInputDraft}
        onSend={(text) => {
          sendMessage(text);
          setInputDraft("");
        }}
        onStop={stop}
        loading={loading}
        disabled={!isConfigured}
      />
    </div>
  );
}
